import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

function fmtTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function MixerCard({ mixer, allFgs, allSfgs, onSave }) {
  const showToast = useToast();
  const [editing,    setEditing]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [assigning,  setAssigning]  = useState(false);
  const [removing,   setRemoving]   = useState(null);      // "FG:name" or "SFG:name"
  const [selected,   setSelected]   = useState(new Set()); // Set of "<type>:<id>"
  const [form, setForm] = useState({
    capacityMinKg:    mixer.capacity_min_kg,
    capacityMaxKg:    mixer.capacity_max_kg,
    batchTimeMinutes: mixer.batch_time_minutes,
    active:           mixer.active,
    notes:            mixer.notes ?? "",
  });
  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const assignedFgNames  = new Set(mixer.grades    ?? []);
  const assignedSfgNames = new Set(mixer.sfgGrades ?? []);

  const unassignedFgs  = useMemo(
    () => allFgs.filter(f  => !assignedFgNames.has(f.name  ?? f.id)),
    [allFgs,  mixer.grades]    // eslint-disable-line
  );
  const unassignedSfgs = useMemo(
    () => allSfgs.filter(s => !assignedSfgNames.has(s.name ?? s.id)),
    [allSfgs, mixer.sfgGrades] // eslint-disable-line
  );

  async function handleSave() {
    setSaving(true);
    try {
      await api.updateMixer(mixer.id, {
        capacityMinKg:    Number(form.capacityMinKg),
        capacityMaxKg:    Number(form.capacityMaxKg),
        batchTimeMinutes: Number(form.batchTimeMinutes),
        active:           form.active,
        notes:            form.notes || null,
      });
      setEditing(false);
      showToast("Mixer updated");
      onSave?.();
    } catch (e) {
      alert("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  function toggleItem(val) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set([
      ...unassignedFgs.map(f => `FG:${f.id}`),
      ...unassignedSfgs.map(s => `SFG:${s.id}`),
    ]));
  }

  async function handleAssign() {
    if (selected.size === 0) return;
    const count = selected.size;
    setAssigning(true);
    try {
      await Promise.all([...selected].map(async item => {
        const [type, itemId] = item.split(/:(.+)/);
        if (type === "FG") {
          const current = await api.fetchFgMixers(itemId);
          if (!current.includes(mixer.id))
            await api.saveFgMixers(itemId, [...current, mixer.id]);
        } else {
          const current = await api.fetchSfgMixers(itemId);
          if (!current.includes(mixer.id))
            await api.saveSfgMixers(itemId, [...current, mixer.id]);
        }
      }));
      setSelected(new Set());
      showToast(count === 1 ? "Grade assigned" : `${count} grades assigned`);
      onSave?.();
    } catch (e) {
      alert("Assign failed: " + e.message);
    } finally {
      setAssigning(false);
    }
  }

  async function handleRemove(type, gradeName) {
    const key = `${type}:${gradeName}`;
    setRemoving(key);
    try {
      if (type === "FG") {
        const fg = allFgs.find(f => (f.name ?? f.id) === gradeName);
        if (!fg) return;
        const current = await api.fetchFgMixers(fg.id);
        await api.saveFgMixers(fg.id, current.filter(id => id !== mixer.id));
      } else {
        const sfg = allSfgs.find(s => (s.name ?? s.id) === gradeName);
        if (!sfg) return;
        const current = await api.fetchSfgMixers(sfg.id);
        await api.saveSfgMixers(sfg.id, current.filter(id => id !== mixer.id));
      }
      showToast("Grade removed", "info");
      onSave?.();
    } catch (e) {
      alert("Remove failed: " + e.message);
    } finally {
      setRemoving(null);
    }
  }

  const batchesPerDay540 = mixer.batch_time_minutes ? Math.floor(540 / mixer.batch_time_minutes) : null;
  const batchesPerDay300 = mixer.batch_time_minutes ? Math.floor(300 / mixer.batch_time_minutes) : null;

  return (
    <div className="card" style={{ marginBottom: 0 }}>
      {/* Header */}
      <div style={{
        padding: "10px 14px", borderBottom: "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 10,
        background: mixer.active ? "white" : "#f9fafb",
      }}>
        <span style={{
          fontWeight: 700, fontSize: 13, color: mixer.active ? "#1d4ed8" : "#9ca3af",
          background: mixer.active ? "#eff6ff" : "#f3f4f6",
          border: `1px solid ${mixer.active ? "#bfdbfe" : "#e5e7eb"}`,
          borderRadius: 4, padding: "2px 10px", minWidth: 72, textAlign: "center",
        }}>
          {mixer.name}
        </span>
        <span style={{ fontSize: 11, color: "#6b7280" }}>
          {mixer.blade_type} blade · {mixer.heating ? "Heating ✓" : "No heating"}
        </span>
        {!mixer.active && (
          <span style={{ fontSize: 10, background: "#fef2f2", color: "#b91c1c", borderRadius: 3, padding: "1px 6px", fontWeight: 600 }}>INACTIVE</span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {!editing && (
            <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }}
              onClick={() => setEditing(true)}>Edit</button>
          )}
          {editing && (
            <>
              <button className="btn btn-primary" style={{ fontSize: 11, padding: "3px 10px" }}
                onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }}
                onClick={() => setEditing(false)}>Cancel</button>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {/* Left: specs */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Capacity & Timing</div>

          {editing ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ fontSize: 11, width: 110 }}>Min Capacity (KG)</label>
                <input className="form-input" type="number" min="0" style={{ width: 90 }}
                  value={form.capacityMinKg}
                  onChange={e => setF("capacityMinKg", e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ fontSize: 11, width: 110 }}>Max Capacity (KG)</label>
                <input className="form-input" type="number" min="0" style={{ width: 90 }}
                  value={form.capacityMaxKg}
                  onChange={e => setF("capacityMaxKg", e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ fontSize: 11, width: 110 }}>Batch Time (min)</label>
                <input className="form-input" type="number" min="1" style={{ width: 90 }}
                  value={form.batchTimeMinutes}
                  onChange={e => setF("batchTimeMinutes", e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ fontSize: 11, width: 110 }}>Active</label>
                <input type="checkbox" checked={form.active}
                  onChange={e => setF("active", e.target.checked)} />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <label style={{ fontSize: 11, width: 110, paddingTop: 4 }}>Notes</label>
                <textarea className="form-input" style={{ width: 200, fontSize: 11, resize: "vertical", minHeight: 48 }}
                  value={form.notes}
                  onChange={e => setF("notes", e.target.value)} />
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <span style={{ color: "#6b7280", width: 110 }}>Capacity:</span>
                <span style={{ fontWeight: 600 }}>{mixer.capacity_min_kg.toLocaleString()} – {mixer.capacity_max_kg.toLocaleString()} KG</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <span style={{ color: "#6b7280", width: 110 }}>Batch time:</span>
                <span style={{ fontWeight: 600 }}>
                  {mixer.batch_time_minutes ? `${fmtTime(mixer.batch_time_minutes)} (${mixer.batch_time_minutes} min)` : "—"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <span style={{ color: "#6b7280", width: 110 }}>Batches/day:</span>
                <span style={{ fontWeight: 600 }}>
                  {batchesPerDay540 != null ? `${batchesPerDay540} (Mon–Sat) · ${batchesPerDay300} (Fri)` : "—"}
                </span>
              </div>
              {mixer.notes && (
                <div style={{ display: "flex", gap: 8 }}>
                  <span style={{ color: "#6b7280", width: 110 }}>Notes:</span>
                  <span style={{ color: "#374151" }}>{mixer.notes}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: assigned FGs + assign control */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            Grades ({(mixer.grades ?? []).length + (mixer.sfgGrades ?? []).length})
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
            {(mixer.grades ?? []).map(g => (
              <span key={`fg-${g}`} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 10, fontFamily: "monospace", padding: "2px 6px 2px 8px",
                background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 3,
              }}>
                {g}
                <button onClick={() => handleRemove("FG", g)} disabled={removing === `FG:${g}`}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 11, padding: 0, lineHeight: 1, opacity: removing === `FG:${g}` ? 0.4 : 1 }}
                  title="Remove FG from this mixer">✕</button>
              </span>
            ))}
            {(mixer.sfgGrades ?? []).map(g => (
              <span key={`sfg-${g}`} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 10, fontFamily: "monospace", padding: "2px 6px 2px 8px",
                background: "#fdf4ff", border: "1px solid #e9d5ff", borderRadius: 3, color: "#6b21a8",
              }}>
                {g} <span style={{ fontSize: 9, opacity: 0.7 }}>SFG</span>
                <button onClick={() => handleRemove("SFG", g)} disabled={removing === `SFG:${g}`}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#a855f7", fontSize: 11, padding: 0, lineHeight: 1, opacity: removing === `SFG:${g}` ? 0.4 : 1 }}
                  title="Remove SFG from this mixer">✕</button>
              </span>
            ))}
            {(mixer.grades ?? []).length === 0 && (mixer.sfgGrades ?? []).length === 0 && (
              <span style={{ fontSize: 11, color: "#9ca3af" }}>No grades assigned</span>
            )}
          </div>

          {/* Assign multi-select */}
          {unassignedFgs.length === 0 && unassignedSfgs.length === 0 ? (
            <span style={{ fontSize: 11, color: "#9ca3af" }}>All grades assigned</span>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: "#6b7280" }}>
                  {selected.size > 0 ? `${selected.size} selected` : "Select grades to assign"}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={selectAll}
                    style={{ fontSize: 10, background: "none", border: "none", color: "#2563eb", cursor: "pointer", padding: 0 }}>
                    Select all
                  </button>
                  {selected.size > 0 && (
                    <button onClick={() => setSelected(new Set())}
                      style={{ fontSize: 10, background: "none", border: "none", color: "#6b7280", cursor: "pointer", padding: 0 }}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div style={{ maxHeight: 130, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 4, padding: "4px 8px", fontSize: 11 }}>
                {unassignedFgs.length > 0 && (
                  <>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", padding: "3px 0 2px" }}>Finished Goods</div>
                    {unassignedFgs.map(f => (
                      <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0", cursor: "pointer" }}>
                        <input type="checkbox" checked={selected.has(`FG:${f.id}`)} onChange={() => toggleItem(`FG:${f.id}`)} />
                        <span style={{ fontFamily: "monospace" }}>{f.name ?? f.id}</span>
                      </label>
                    ))}
                  </>
                )}
                {unassignedSfgs.length > 0 && (
                  <>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", padding: "3px 0 2px", marginTop: unassignedFgs.length > 0 ? 4 : 0 }}>Semi-Finished</div>
                    {unassignedSfgs.map(s => (
                      <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0", cursor: "pointer" }}>
                        <input type="checkbox" checked={selected.has(`SFG:${s.id}`)} onChange={() => toggleItem(`SFG:${s.id}`)} />
                        <span style={{ fontFamily: "monospace", color: "#6b21a8" }}>{s.name ?? s.id}</span>
                      </label>
                    ))}
                  </>
                )}
              </div>
              <button className="btn btn-primary"
                style={{ fontSize: 11, padding: "3px 10px", marginTop: 6, whiteSpace: "nowrap" }}
                disabled={selected.size === 0 || assigning} onClick={handleAssign}>
                {assigning ? "Assigning…" : selected.size > 1 ? `Assign ${selected.size} grades` : "Assign"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function PageMixerConfig({ myPerms = [] }) {
  const [mixers,  setMixers]  = useState([]);
  const [allFgs,  setAllFgs]  = useState([]);
  const [allSfgs, setAllSfgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  function load() {
    setLoading(true); setError(null);
    Promise.all([api.fetchMixers(), api.fetchFGs(), api.fetchSFGs()])
      .then(([mx, fgs, sfgs]) => { setMixers(mx); setAllFgs(fgs); setAllSfgs(sfgs); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const active   = mixers.filter(m => m.active);
  const inactive = mixers.filter(m => !m.active);

  return (
    <div style={{ padding: "20px 24px" }}>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div className="page-header-left">
          <div className="page-title">Mixer Configuration</div>
          <div className="page-subtitle">Capacity, batch timings and grade assignments for all {mixers.length} mixers</div>
        </div>
        <button className="btn" onClick={load} disabled={loading}>↻ Refresh</button>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading…</div>}
      {error   && <div className="alert-warn">{error}</div>}

      {!loading && !error && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(560px, 1fr))", gap: 14, marginBottom: 20 }}>
            {active.map(m => <MixerCard key={m.id} mixer={m} allFgs={allFgs} allSfgs={allSfgs} onSave={load} />)}
          </div>

          {inactive.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                Inactive Mixers
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(560px, 1fr))", gap: 14 }}>
                {inactive.map(m => <MixerCard key={m.id} mixer={m} allFgs={allFgs} allSfgs={allSfgs} onSave={load} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
